// =============================================================================
// Auth Debugger - 帮助诊断认证问题
// =============================================================================

import React, { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle, RefreshCw, LogIn } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const AuthDebugger = () => {
  const navigate = useNavigate();
  const [authStatus, setAuthStatus] = useState({
    hasToken: false,
    tokenValid: false,
    tokenExpired: false,
    serverReachable: false,
    loading: true
  });

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    setAuthStatus(prev => ({ ...prev, loading: true }));

    const token = localStorage.getItem('auth_token');
    const hasToken = !!token;

    let tokenValid = false;
    let tokenExpired = false;
    let serverReachable = false;

    if (hasToken) {
      try {
        // 尝试解析 JWT token
        const payload = JSON.parse(atob(token.split('.')[1]));
        const now = Date.now() / 1000;
        tokenExpired = payload.exp < now;

        // 测试服务器连接
        const response = await fetch('/api/auth/verify', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        serverReachable = true;
        tokenValid = response.ok;
      } catch (error) {
        console.error('Auth check failed:', error);
        serverReachable = false;
      }
    }

    setAuthStatus({
      hasToken,
      tokenValid,
      tokenExpired,
      serverReachable,
      loading: false
    });
  };

  const clearTokenAndRedirect = () => {
    localStorage.removeItem('auth_token');
    navigate('/login');
  };

  const StatusItem = ({ icon: Icon, label, status, description }) => (
    <div className="flex items-center gap-3 p-3 border rounded-lg">
      <Icon className={`w-5 h-5 ${
        status === 'success' ? 'text-green-600' : 
        status === 'error' ? 'text-red-600' : 
        'text-yellow-600'
      }`} />
      <div className="flex-1">
        <div className="font-medium">{label}</div>
        <div className="text-sm text-muted-foreground">{description}</div>
      </div>
      <div className={`px-2 py-1 rounded text-xs font-medium ${
        status === 'success' ? 'bg-green-100 text-green-800' :
        status === 'error' ? 'bg-red-100 text-red-800' :
        'bg-yellow-100 text-yellow-800'
      }`}>
        {status === 'success' ? 'OK' : status === 'error' ? 'FAIL' : 'WARN'}
      </div>
    </div>
  );

  if (authStatus.loading) {
    return (
      <div className="p-6 text-center">
        <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4" />
        <p>Checking authentication status...</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">Authentication Status</h2>
        <p className="text-muted-foreground">
          Diagnostic information about your authentication status
        </p>
      </div>

      <div className="space-y-4 mb-6">
        <StatusItem
          icon={authStatus.hasToken ? CheckCircle : AlertCircle}
          label="Authentication Token"
          status={authStatus.hasToken ? 'success' : 'error'}
          description={authStatus.hasToken ? 'Token found in localStorage' : 'No token found'}
        />

        <StatusItem
          icon={authStatus.serverReachable ? CheckCircle : AlertCircle}
          label="Server Connection"
          status={authStatus.serverReachable ? 'success' : 'error'}
          description={authStatus.serverReachable ? 'Server is reachable' : 'Cannot reach server'}
        />

        {authStatus.hasToken && (
          <StatusItem
            icon={authStatus.tokenExpired ? AlertCircle : CheckCircle}
            label="Token Expiration"
            status={authStatus.tokenExpired ? 'error' : 'success'}
            description={authStatus.tokenExpired ? 'Token has expired' : 'Token is still valid'}
          />
        )}

        {authStatus.hasToken && authStatus.serverReachable && (
          <StatusItem
            icon={authStatus.tokenValid ? CheckCircle : AlertCircle}
            label="Token Validation"
            status={authStatus.tokenValid ? 'success' : 'error'}
            description={authStatus.tokenValid ? 'Server accepts the token' : 'Server rejected the token'}
          />
        )}
      </div>

      <div className="bg-secondary/50 p-4 rounded-lg mb-6">
        <h3 className="font-semibold mb-2">Recommendations</h3>
        <div className="text-sm space-y-2">
          {!authStatus.hasToken && (
            <p>• You need to log in to access the workspace features.</p>
          )}
          {authStatus.hasToken && authStatus.tokenExpired && (
            <p>• Your session has expired. Please log in again.</p>
          )}
          {authStatus.hasToken && !authStatus.tokenValid && (
            <p>• Your authentication token is invalid. Please log in again.</p>
          )}
          {!authStatus.serverReachable && (
            <p>• Cannot connect to the server. Please check if the server is running.</p>
          )}
          {authStatus.hasToken && authStatus.tokenValid && authStatus.serverReachable && (
            <p>• ✅ Everything looks good! You should be able to access all features.</p>
          )}
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={checkAuthStatus}
          className="btn btn-ghost"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh Status
        </button>

        {(!authStatus.hasToken || authStatus.tokenExpired || !authStatus.tokenValid) && (
          <button
            onClick={clearTokenAndRedirect}
            className="btn btn-default"
          >
            <LogIn className="w-4 h-4 mr-2" />
            Go to Login
          </button>
        )}
      </div>

      <div className="mt-6 p-4 bg-muted/50 rounded-lg">
        <h4 className="font-medium mb-2">Debug Information</h4>
        <pre className="text-xs text-muted-foreground overflow-x-auto">
          {JSON.stringify(authStatus, null, 2)}
        </pre>
      </div>
    </div>
  );
};

export default AuthDebugger;
